import util from '../lib/util';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
class ImportSource extends Base implements EntityType {
    importSourceId: uuid;
    name?: string | null;
    useFiles: boolean;
    apiKey?: string | null;
    apiKeyValid: boolean;
    fullDocuments: any[];
    outputMap?: any | null;
    docLink?: string | null;
    uses: string[];
    website?: string | null;
    constructor(doc: typeof ImportSource | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            importSourceId: genUUID(),
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "importSource";
        // @ts-ignore
        this.importSourceId = doc._importSourceId || doc.importSourceId || doc.importsourceid || genUUID();
        // @ts-ignore
        this.name = doc._name || doc.name || null;
        // @ts-ignore
        this.useFiles = util.defaultBoolean([doc.useFiles, doc._useFiles, doc.usefiles, false]);
        // @ts-ignore
        this.apiKey = doc._apiKey || doc.apiKey || doc.apikey || null;
        // @ts-ignore
        this.apiKeyValid = util.defaultBoolean([doc.apiKeyValid, doc._apiKeyValid, doc.apikeyvalid, false]);
        // @ts-ignore
        this.fullDocuments = doc._fullDocuments || doc.fullDocuments || doc.fulldocuments || [];
        // @ts-ignore
        this.outputMap = doc._outputMap || doc.outputMap || doc.outputmap || {};
        // @ts-ignore
        this.docLink = doc._docLink || doc.docLink || doc.doclink || null;
        // @ts-ignore
        this.uses = doc._uses || doc.uses || [];
        // @ts-ignore
        this.website = doc._website || doc.website || null;
    }
    get id(): uuid { return this.importSourceId; }
    addFullDocument(fullDocument: any): any {
        if (!fullDocument) throw new ValidationError('No nulls allowed in an array!');
        if (!this.fullDocuments) this.fullDocuments = [];
        this.fullDocuments.push(fullDocument);
        return fullDocument;
    }
    updateFullDocument(fullDocument: any) {
        if (!this.fullDocuments) this.fullDocuments = [];
        const index = this.fullDocuments.findIndex(item => item === fullDocument);
        if (index !== -1) { this.fullDocuments[index] = fullDocument; }
        else this.addFullDocument(fullDocument);
    }
    removeFullDocument(fullDocument: any) {
        if (!this.fullDocuments) return;
        let foundItems = this.fullDocuments?.filter(item => item === fullDocument);
        if (foundItems && foundItems.length > 0) {
            this.fullDocuments = this.fullDocuments.filter(item => !(item === fullDocument));
        }
    }
    abandonFullDocuments() { this.fullDocuments = []; }
    addUs(us: string): string {
        if (!us) throw new ValidationError('No nulls allowed in an array!');
        if (!this.uses) this.uses = [];
        this.uses.push(us);
        return us;
    }
    updateUs(us: string) {
        if (!this.uses) this.uses = [];
        const index = this.uses.findIndex(item => item === us);
        if (index !== -1) { this.uses[index] = us; }
        else this.addUs(us);
    }
    removeUs(us: string) {
        if (!this.uses) return;
        let foundItems = this.uses?.filter(item => item === us);
        if (foundItems && foundItems.length > 0) {
            this.uses = this.uses.filter(item => !(item === us));
        }
    }
    abandonUses() { this.uses = []; }
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique id for this import/export"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}]; }
    toLowerCase() { return this.importSourceId; }
    static override getNonRefArrayFields(): string[] { return ["fullDocuments", "uses"]; }
    static override getDefaultEntries(): ImportSource[] {
        return [];
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'importSource',
    dataArea: 'Firm Settings',
    table: 'IMPORT_SOURCE',
    comment: `A configured third-party product from which a firm's existing data can be migrated into Outlaw Practice. New configurations use supported sources such as Bill4Time; historical records may reference retired sources such as Pipedrive. Stores the product name and the firm's API key for that product; runtime state such as fetched documents, mapped output, and API-key validity are carried as transient fields during the import process.`,
    fields: {
        importSourceId: {
            type: 'uuid',
            required: true,
            pk: true,
            default: '__UUID__',
            comment: 'Unique id for this import/export'
        },

        name: {
            type: 'string', comment: 'Name of the product being imported'
        },

        useFiles: {
            type: 'boolean', comment: 'Does this source use files?'
        },

        apiKey: {
            type: 'string', comment: 'If using an API, this is the customer key'
        },

        /**
         * Transient: Is the api key valid?
         */
        apiKeyValid: {
            type: 'boolean', transient: true, comment: 'Is the key valid?'
        },

        /**
         * Transient: Full document info, not just ids, with all document text in plainText
         */
        fullDocuments: {
            type: 'json[]', transient: true, comment: 'The full documents (required for the hook)'
        },

        /**
         * Transient: The import results, mapped with string => object[]
         */
        outputMap: {
            type: 'json', transient: true, comment: 'Imported results (mapped from input sources to output schema)'
        },

        /**
         * Transient: A URL with instructions to export files, or to get an API key
         */
        docLink: {
            type: 'string', transient: true, comment: 'Documentation link(s) for this import source'
        },

        /**
         * Transient: what access methods does this source support?
         * Valid values are apiKey and files.
         */
        uses: {
            type: 'string[]', transient: true, comment: 'The access methods this source supports (e.g. API key, file upload, OAuth)'
        },

        /**
         * Transient: the full api of the product.
         */
        website: {
            type: 'string', transient: true, comment: 'URL for the product (landing page)'
        }
    }
});
    }
}
export default ImportSource;
